import type { AwsS3UploadParameters } from '@uppy/aws-s3'
import AwsS3 from '@uppy/aws-s3'
import Uppy from '@uppy/core'
import { useEffect, useRef, useState } from 'react'

export interface FileMeta {
	name: string
	size: number
	type: string
}

export type FileUploadState =
	| ({ status: 'uploading'; url: string; progress: number } & FileMeta)
	| ({ status: 'done'; url: string; backendId: string } & FileMeta)
	| ({ status: 'error'; url: string; error: Error } & FileMeta)

export interface CompletedUpload extends FileMeta {
	id: string
	url: string
	backendId: string
}

export interface FailedUpload extends FileMeta {
	id: string
	error: Error
}

export interface UploadSettledResult {
	successful: CompletedUpload[]
	failed: FailedUpload[]
}

/**
 * Request presigned upload parameters for a single file. Implementations call
 * the backend and return the upload instructions plus the persistent asset id.
 * `id` matches `upload()`'s return value and the `files` keys, so callers that
 * track per-file state (e.g. an attachment kind) can look it up here.
 */
export type PresignFn = (
	file: FileMeta,
	id: string
) => Promise<{
	upload: AwsS3UploadParameters
	backendId: string
}>

export function useUpload(options: {
	concurrency?: number
	presign: PresignFn
	// Fires once per upload batch when it settles (mirrors Uppy's `complete`).
	// A single-file retry does not re-fire this; track recovery via `files`.
	onComplete?: (result: UploadSettledResult) => void
}) {
	const [files, setFiles] = useState<Record<string, FileUploadState>>({})
	const uppyRef =
		useRef<Uppy<{ backendId?: string }, { uploadURL?: string }>>(null)

	const optionsRef = useRef(options)
	optionsRef.current = options

	const filesRef = useRef(files)
	filesRef.current = files

	useEffect(() => {
		const instance = new Uppy<
			{ backendId?: string },
			{ uploadURL?: string }
		>({ autoProceed: true })
		uppyRef.current = instance

		instance.use(AwsS3, {
			shouldUseMultipart: false,
			limit: optionsRef.current.concurrency ?? 3,
			getUploadParameters: async (uppyFile) => {
				const file = uppyFile.data
				if (!(file instanceof File)) {
					throw new Error('expected File')
				}
				const { upload, backendId } = await optionsRef.current.presign(
					{
						name: file.name,
						size: file.size,
						type: file.type
					},
					uppyFile.id
				)

				instance.setFileMeta(uppyFile.id, { backendId })
				return upload
			}
		})

		instance.on('file-added', (uppyFile) => {
			const file = uppyFile.data
			if (!(file instanceof File)) {
				return
			}
			setFiles((prev) => ({
				...prev,
				[uppyFile.id]: {
					status: 'uploading',
					url: URL.createObjectURL(file),
					name: file.name,
					size: file.size,
					type: file.type,
					progress: 0
				}
			}))
		})

		instance.on('upload-progress', (uppyFile, progress) => {
			if (!uppyFile) {
				return
			}
			const total = progress.bytesTotal ?? 0
			const ratio = total > 0 ? progress.bytesUploaded / total : 0
			const nextProgress = Math.round(ratio * 100)
			setFiles((prev) => {
				const current = prev[uppyFile.id]
				if (
					!current ||
					current.status !== 'uploading' ||
					current.progress === nextProgress
				) {
					return prev
				}
				return {
					...prev,
					[uppyFile.id]: { ...current, progress: nextProgress }
				}
			})
		})

		instance.on('upload-success', (uppyFile) => {
			if (!uppyFile) {
				return
			}
			setFiles((prev) => {
				const current = prev[uppyFile.id]
				if (!current) {
					return prev
				}
				// Keep the blob url so consumers can preview the file for its
				// whole lifetime; `remove()` and unmount revoke it.
				return {
					...prev,
					[uppyFile.id]: {
						status: 'done',
						url: current.url,
						name: current.name,
						size: current.size,
						type: current.type,
						backendId:
							instance.getFile(uppyFile.id)?.meta.backendId ?? ''
					}
				}
			})
		})

		instance.on('upload-error', (uppyFile, error) => {
			if (!uppyFile) {
				return
			}
			setFiles((prev) => {
				const current = prev[uppyFile.id]
				if (!current) {
					return prev
				}
				return {
					...prev,
					[uppyFile.id]: {
						status: 'error',
						url: current.url,
						name: current.name,
						size: current.size,
						type: current.type,
						error: new Error(error.message)
					}
				}
			})
		})

		instance.on('complete', (result) => {
			const successful = (result.successful ?? []).map((file) => ({
				id: file.id,
				url: file.uploadURL ?? '',
				backendId: file.meta.backendId ?? '',
				name: file.name,
				size: file.size ?? 0,
				type: file.type
			}))
			const failed = (result.failed ?? []).map((file) => ({
				id: file.id,
				name: file.name,
				size: file.size ?? 0,
				type: file.type,
				error: new Error(file.error ?? '')
			}))
			optionsRef.current.onComplete?.({ successful, failed })
		})

		return () => {
			for (const file of Object.values(filesRef.current)) {
				if (file.url.startsWith('blob:')) {
					URL.revokeObjectURL(file.url)
				}
			}
			instance.destroy()
			uppyRef.current = null
		}
	}, [])

	/** Returns the upload id, or null when Uppy rejects the file — it throws on duplicate adds (same name/size/type as a file still in `files`). */
	function upload(file: File) {
		try {
			return (
				uppyRef.current?.addFile({
					data: file,
					name: file.name,
					type: file.type
				}) ?? null
			)
		} catch {
			return null
		}
	}

	function remove(id: string) {
		uppyRef.current?.removeFile(id)
		setFiles((prev) => {
			const current = prev[id]
			if (!current) {
				return prev
			}
			if (current.url.startsWith('blob:')) {
				URL.revokeObjectURL(current.url)
			}
			const next = { ...prev }
			delete next[id]
			return next
		})
	}

	function retry(id: string) {
		setFiles((prev) => {
			const current = prev[id]
			if (!current || current.status !== 'error') {
				return prev
			}
			return {
				...prev,
				[id]: {
					status: 'uploading',
					url: current.url,
					name: current.name,
					size: current.size,
					type: current.type,
					progress: 0
				}
			}
		})
		uppyRef.current?.retryUpload(id)
	}

	function getFile(id: string) {
		const file = uppyRef.current?.getFile(id)?.data
		return file instanceof File ? file : null
	}

	return { files, upload, remove, retry, getFile }
}
